import {
  FieldClass,
  FieldType,
  ITableColumnFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { assertNotNil } from '../../../../utils/types';
import { defaultEmptyDraftEdits } from '../../../../redux/slices/dataModeler/dataModelerSlice';
import { useCallback, useMemo } from 'react';
import {
  getAllModelFields,
  getDraftCustomColumns,
  getViewModelFields,
  listToHashMapFromKey,
  useAvailableFieldsToExtend,
} from '../utils';
import {
  dataModelerAddDraftFieldData,
  IDataModelerDraftEdits,
} from '../../../../redux/slices/dataModeler';
import { ICdwTableColumnData } from '../DataModelerCdwTableGrid/types';
import { DATA_MODEL_FIELD_TYPE_CONFIG } from '../../../../redux/slices/dataModeler/utils';

export const COLUMN_SELECTOR_WIDTH = 240;

export interface ISelectableFieldItem {
  fieldKey: string;
  fieldName: string;
  viewColumn: string;
  type: FieldType;
  fieldClass: FieldClass;
  // This is set to true only when creating new field in-place
  justCreated: boolean;
}

export const useAvailableSchemaFieldsForSelection = ({
  viewKey,
}: {
  viewKey?: string;
}): ISelectableFieldItem[] => {
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;
  return useMemo(() => {
    const existingFieldMetas = !viewKey
      ? getAllModelFields(schemaObj)
      : getViewModelFields(schemaObj, viewKey);
    const updateFieldInputsByFieldKey = listToHashMapFromKey(
      draftEdits.updatedFieldsInputs,
      'key',
    );
    return [
      ...existingFieldMetas.map((fieldMeta) => {
        const updateInput = updateFieldInputsByFieldKey[fieldMeta.key];
        return {
          fieldKey: fieldMeta.key,
          fieldName: updateInput?.name ?? fieldMeta.name,
          viewColumn: fieldMeta.column,
          type: fieldMeta.type,
          fieldClass: fieldMeta.fieldClass,
          justCreated: false,
        };
      }),
      ...draftEdits.draftFieldsData
        .flatMap(({ addInput }) => (addInput ? [addInput] : []))
        .filter((fieldInput) => !viewKey || fieldInput.viewKey === viewKey)
        .map((fieldInput) => {
          return {
            fieldKey: fieldInput.key,
            fieldName: fieldInput.name ?? fieldInput.viewColumn,
            viewColumn: fieldInput.viewColumn,
            type: fieldInput.typeInput.type,
            fieldClass: fieldInput.typeInput.fieldClass,
            justCreated: false,
          };
        }),
    ].sort((a, b) => a.fieldName.localeCompare(b.fieldName));
  }, [
    draftEdits.draftFieldsData,
    draftEdits.updatedFieldsInputs,
    schemaObj,
    viewKey,
  ]);
};

const getAvailableColumnsWithTypeOverrides = (
  tableKey: string,
  draftEdits: IDataModelerDraftEdits,
  availableColumnsFromServer: ITableColumnFieldsFragment[],
): ITableColumnFieldsFragment[] => {
  const typeOverrides = draftEdits.draftFieldsData.flatMap((draft) => {
    return draft.tableKey === tableKey
      ? draft.addInput
        ? [
            {
              column: draft.column.column,
              type: draft.addInput.typeInput.type,
            },
          ]
        : draft.extendInput
          ? [
              {
                column: draft.column.column,
                type: draft.extendInput.typeInput.type,
              },
            ]
          : []
      : [];
  });
  const typeOverridesByColumn = listToHashMapFromKey(typeOverrides, 'column');
  return availableColumnsFromServer.map((tableColumn) => {
    const typeOverride = typeOverridesByColumn[tableColumn.column];
    if (!typeOverride) {
      return tableColumn;
    }
    return {
      ...tableColumn,
      type: typeOverride.type,
    };
  });
};

export const useAvailableColumnsForSelection = ({
  viewKey,
  tableKey,
  availableColumnsFromServer,
}: {
  viewKey: string;
  tableKey: string;
  availableColumnsFromServer: ITableColumnFieldsFragment[];
}): {
  items: ITableColumnFieldsFragment[];
  addDimensionFieldIfNotSelected: (column: string) => void;
} => {
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const dispatch = useAppDispatch();
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;

  // XXX handle type overrides which can come from newly mapped columns
  // (and can alter what is available based on type)
  const availableColumnsWithTypeOverrides = useMemo(
    () =>
      getAvailableColumnsWithTypeOverrides(
        tableKey,
        draftEdits,
        availableColumnsFromServer,
      ),
    [tableKey, draftEdits, availableColumnsFromServer],
  );

  const existingColumns = useMemo(() => {
    const existingFieldMetas = getViewModelFields(schemaObj, viewKey);
    const existingColumns = new Set<string>();
    existingFieldMetas.forEach((fieldMeta) => {
      const tableColumn = fieldMeta.tableFields.find(
        (tableField) => tableField.tableKey === tableKey,
      );
      if (tableColumn) {
        existingColumns.add(tableColumn.column);
      }
    });
    return existingColumns;
  }, [schemaObj, tableKey, viewKey]);
  const addedVirtualColumns = getDraftCustomColumns(
    viewKey,
    tableKey,
    draftEdits.draftFieldsData,
  );
  const items = useMemo((): ITableColumnFieldsFragment[] => {
    return [...availableColumnsWithTypeOverrides, ...addedVirtualColumns].sort(
      (a, b) => a.column.localeCompare(b.column),
    );
  }, [addedVirtualColumns, availableColumnsWithTypeOverrides]);
  const addDimensionFieldIfNotSelected = useCallback(
    (column: string) => {
      if (existingColumns.has(column)) {
        return;
      }
      if (schemaObj.fact.key === viewKey) {
        console.error('addDimensionFieldIfNotSelected used with a fact table');
        return;
      }
      if (
        draftEdits.draftFieldsData.some(
          ({ addInput }) =>
            addInput &&
            addInput.viewKey === viewKey &&
            addInput.tableKey === tableKey &&
            addInput.column === column,
        )
      ) {
        return;
      }
      const tableColumnToAdd = availableColumnsFromServer.find(
        (tableColumn) => tableColumn.column === column,
      );
      if (tableColumnToAdd) {
        dispatch(
          dataModelerAddDraftFieldData({
            viewKey,
            tableKey,
            column: tableColumnToAdd,
          }),
        );
      }
    },
    [
      availableColumnsFromServer,
      dispatch,
      draftEdits.draftFieldsData,
      existingColumns,
      schemaObj.fact.key,
      tableKey,
      viewKey,
    ],
  );
  return {
    items,
    addDimensionFieldIfNotSelected,
  };
};

export const useAvailableFieldItemsToExtend = ({
  tableKey,
  columnData,
}: {
  tableKey: string;
  columnData: ICdwTableColumnData;
}): { current: ISelectableFieldItem | null; items: ISelectableFieldItem[] } => {
  const columnType =
    columnData.addFieldInput?.typeInput.type ??
    columnData.extendFieldInput?.typeInput.type ??
    columnData.type;
  const filterTypes = DATA_MODEL_FIELD_TYPE_CONFIG[columnType].canExtend;
  const { existingUnmappedInTable, addedDraftsUnmappedInTable } =
    useAvailableFieldsToExtend({ tableKey, filterTypes, columnData });
  return useMemo(() => {
    const existingUnmapped = existingUnmappedInTable.map((fieldMeta) => {
      return {
        fieldKey: fieldMeta.key,
        fieldName: fieldMeta.name,
        viewColumn: fieldMeta.column,
        type: fieldMeta.type,
        fieldClass: fieldMeta.fieldClass,
        justCreated: false,
      };
    });
    const addedDraftsUnmapped = addedDraftsUnmappedInTable.map((addInput) => {
      assertNotNil(addInput);
      return {
        fieldKey: addInput.key,
        fieldName: addInput.name ?? addInput.viewColumn,
        viewColumn: addInput.viewColumn,
        type: addInput.typeInput.type,
        fieldClass: addInput.typeInput.fieldClass,
        justCreated: false,
      };
    });
    const items = [...existingUnmapped, ...addedDraftsUnmapped].sort((a, b) =>
      a.fieldName.localeCompare(b.fieldName),
    );
    const currentExistingField = columnData.fieldMeta
      ? {
          fieldKey: columnData.fieldMeta.key,
          fieldName: columnData.fieldMeta.name,
          viewColumn: columnData.fieldMeta.column,
          type: columnData.fieldMeta.type,
          fieldClass: columnData.fieldMeta.fieldClass,
          justCreated: false,
        }
      : null;
    const currentAddField: ISelectableFieldItem | null =
      columnData?.addFieldInput
        ? {
            fieldKey: columnData.addFieldInput.key,
            fieldName:
              columnData.addFieldInput.name ??
              columnData.addFieldInput.viewColumn,
            viewColumn: columnData.addFieldInput.viewColumn,
            type: columnData.addFieldInput.typeInput.type,
            fieldClass: columnData.addFieldInput.typeInput.fieldClass,
            justCreated: true,
          }
        : null;
    const currentExtendField =
      items.find(
        (item) => item.fieldKey === columnData.extendFieldInput?.fieldKey,
      ) ?? null;

    const current: ISelectableFieldItem | null =
      currentExistingField ?? currentAddField ?? currentExtendField;
    if (currentExistingField || currentAddField) {
      assertNotNil(current);
      if (!items.some((item) => current.fieldKey === item.fieldKey)) {
        items.splice(0, 0, current);
      }
    }
    return {
      current,
      items,
    };
  }, [
    existingUnmappedInTable,
    addedDraftsUnmappedInTable,
    columnData.addFieldInput,
    columnData.extendFieldInput,
    columnData.fieldMeta,
  ]);
};
